import { useEffect, useRef, useState } from 'react';

/** Visible replacement/set review, with one outstanding decision. Changing
 * the working media or leaving the page cancels; no native browser dialogs. */
export function useAssetDecision(identity: string) {
  type Pending = { identity: string; message: string; resolve: (approved: boolean) => void };
  const [pending, setPending] = useState<Pending | null>(null);
  const current = useRef<Pending | null>(null);
  const latestIdentity = useRef(identity);
  latestIdentity.current = identity;
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    current.current?.resolve(false);
    current.current = null;
    setPending(null);
    return () => { current.current?.resolve(false); current.current = null; };
  }, [identity]);
  useEffect(() => { if (pending?.identity === identity) panel.current?.focus(); }, [pending, identity]);
  const requestDecision = (message: string) => new Promise<boolean>(resolve => {
    current.current?.resolve(false);
    const next = { identity: latestIdentity.current, message, resolve };
    current.current = next;
    setPending(next);
  });
  const answer = (approved: boolean) => {
    const decision = current.current;
    if (!decision) return;
    current.current = null;
    setPending(null);
    decision.resolve(approved && decision.identity === latestIdentity.current);
  };
  return { requestDecision, decisionPanel: pending?.identity === identity ?
    <section className="panel" role="group" aria-label="Review asset choices" tabIndex={-1} ref={panel} data-testid="asset-decision-confirm">
      <h3>Review asset choices</h3>
      <p>{pending.message}</p>
      <p>These choices affect the working copy only. Published versions keep their media until you explicitly publish a new version.</p>
      <button type="button" className="btn btn--primary" onClick={() => answer(true)} data-testid="asset-decision-yes">Confirm choices</button>
      <button type="button" className="btn btn--ghost" onClick={() => answer(false)} data-testid="asset-decision-no">Cancel</button>
    </section> : null };
}
